import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { useTranslation } from "react-i18next";

const MONTH_NAMES_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTH_NAMES_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function prevMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export default function MonthlyRecapPreviewDialog({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const MONTHS = lang === "es" ? MONTH_NAMES_ES : MONTH_NAMES_EN;

  const [{ year, month }, setSel] = useState(prevMonth);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    // Revoke previous blob URL to avoid memory leaks between month switches.
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    try {
      const res = await api.get(`/reports/monthly.pdf?year=${year}&month=${month}`, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (e) {
      logger.error("preview pdf", e);
      const msg = e?.response?.data?.detail || t("recap.previewFailed");
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  // Depending on pdfUrl inside would cause re-fetch loops; we clean it up manually.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Clean up blob URL when dialog closes / component unmounts.
  useEffect(() => {
    if (!open && pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [open, pdfUrl]);

  const download = () => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `patrimonio-${year}-${String(month).padStart(2, "0")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const now = new Date();
  const currentYear = now.getFullYear();
  const YEARS = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl w-[92vw] h-[85vh] p-0 flex flex-col overflow-hidden" data-testid="recap-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#EBEBEA]">
          <DialogTitle className="font-display text-2xl">{t("recap.previewTitle")}</DialogTitle>
          <p className="text-sm text-[#5F7368]">{t("recap.previewHint")}</p>
          <div className="flex flex-wrap items-center gap-3 pt-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("recap.month")}</span>
              <Select
                value={String(month)}
                onValueChange={(v) => setSel((s) => ({ ...s, month: parseInt(v, 10) }))}
              >
                <SelectTrigger className="w-[150px]" data-testid="recap-preview-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((mn, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{mn}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("recap.year")}</span>
              <Select
                value={String(year)}
                onValueChange={(v) => setSel((s) => ({ ...s, year: parseInt(v, 10) }))}
              >
                <SelectTrigger className="w-[100px]" data-testid="recap-preview-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-[#FAFAF9] flex items-center justify-center relative">
          {loading && (
            <div className="flex items-center gap-2 text-[#5F7368]" data-testid="recap-preview-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t("recap.loadingPdf")}</span>
            </div>
          )}
          {!loading && errorMsg && (
            <div className="text-sm text-[#D95D39]" data-testid="recap-preview-error">
              {errorMsg}
            </div>
          )}
          {!loading && !errorMsg && pdfUrl && (
            <iframe
              src={pdfUrl}
              title="Monthly recap PDF"
              className="w-full h-full border-0"
              data-testid="recap-preview-iframe"
            />
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#EBEBEA] gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="recap-preview-close">
            {t("recap.close")}
          </Button>
          <Button
            onClick={download}
            disabled={!pdfUrl || loading}
            className="bg-[#0F291E] hover:bg-[#1C7C54]"
            data-testid="recap-preview-download"
          >
            <Download className="w-4 h-4 mr-2" />
            {t("recap.download")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
